
/**
 * Service for handling email newsletter subscriptions
 */

interface SubscribeResponse {
  message?: string;
  error?: string;
  alreadySubscribed?: boolean;
  needsConfirmation?: boolean;
  details?: string;
}

/**
 * Subscribe a user to the email newsletter (requires email confirmation)
 * @param email The user's email address
 * @param firstName Optional first name
 * @param lastName Optional last name
 * @returns Promise resolving to subscription status
 */
export async function subscribeToNewsletter(
  email: string,
  firstName?: string | null,
  lastName?: string | null
): Promise<SubscribeResponse> {
  try {
    console.log('Subscribing to newsletter:', { email, firstName, lastName });
    
    // Use the Supabase client to call the edge function
    const { supabase } = await import('@/integrations/supabase/client');
    
    const response = await supabase.functions.invoke('email-subscribe', {
      body: JSON.stringify({
        email,
        first_name: firstName || null,
        last_name: lastName || null,
        action: 'subscribe'
      })
    });

    console.log('Edge function response:', response);

    if (response.error) {
      console.error('Edge function error:', response.error);
      return { error: response.error.message || 'Failed to subscribe to newsletter' };
    }

    return response.data || { message: 'Subscription processed successfully' };
  } catch (error) {
    console.error('Error subscribing to newsletter:', error);
    return { error: 'Failed to connect to subscription service' };
  }
}

/**
 * Subscribe a user to the email newsletter directly (no confirmation required)
 * Used for users who have already confirmed their email through account signup
 * @param email The user's email address
 * @param firstName Optional first name
 * @param lastName Optional last name
 * @returns Promise resolving to subscription status
 */
export async function subscribeToNewsletterDirectly(
  email: string,
  firstName?: string | null,
  lastName?: string | null
): Promise<SubscribeResponse> {
  try {
    console.log('Subscribing to newsletter directly (no confirmation):', { email, firstName, lastName });

    // Use the Supabase client to call the edge function with direct confirmation
    const { supabase } = await import('@/integrations/supabase/client');

    const response = await supabase.functions.invoke('email-subscribe', {
      body: JSON.stringify({
        email,
        first_name: firstName || null,
        last_name: lastName || null,
        action: 'subscribe_direct' // New action for direct subscription
      })
    });

    console.log('Direct subscription response:', response);

    if (response.error) {
      console.error('Direct subscription error:', response.error);
      return { error: response.error.message || 'Failed to subscribe to newsletter' };
    }

    return response.data || { message: 'Newsletter subscription successful' };
  } catch (error) {
    console.error('Error subscribing to newsletter directly:', error);
    return { error: 'Failed to connect to subscription service' };
  }
}
